import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';
import { isNative } from './platform.js';

/**
 * Sharing and outbound links inside a native web view.
 *
 * The legacy core already calls `navigator.share` and renders
 * `target="_blank"` anchors. Neither works in a wrapped web view — `_blank`
 * opens nothing at all, which reads as a broken app. Rather than rewrite every
 * call site, both are redirected at the platform level.
 */

/**
 * Points `navigator.share` at the native share sheet so existing feature code
 * keeps working unchanged, and so the capability check the core does
 * (`navigator.share ? ... : ...`) reports true on native.
 */
function installSharePolyfill() {
  if (!isNative) return;

  navigator.share = async function nativeShare({ title, text, url } = {}) {
    await Share.share({ title, text, url, dialogTitle: title });
  };

  // The core gates the "Share via…" button on this being present.
  if (!navigator.canShare) {
    navigator.canShare = () => true;
  }
}

/**
 * Opens http(s) links that would otherwise target a new tab in an in-app
 * browser, which keeps the user inside the app and satisfies the store
 * expectation that external content does not replace the app itself.
 */
function installExternalLinkHandler() {
  if (!isNative) return;

  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target instanceof Element && event.target.closest('a[href]');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
      if (!/^https?:\/\//i.test(href)) return;

      event.preventDefault();
      Browser.open({ url: href, presentationStyle: 'popover' }).catch((err) =>
        console.warn('[ironpact] could not open external link', err),
      );
    },
    // Capture so we win over any handler the core attached to the anchor.
    true,
  );
}

export function installLinks() {
  installSharePolyfill();
  installExternalLinkHandler();
}
